import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { handleError } from 'src/app/shared/functions/error-handler';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { ISedmsFormOutput } from 'src/app/shared/interfaces/form/sedms-form-output.model';
import { AppConfigService } from '../../../services/app-config.service';
import { IAppConfig } from '../../../shared/interfaces/app-config';
import { SchoolDetails } from '../models/placement-finder/school-list.model';
import { NpsReferralRequest } from '../state-approved-nps-referral/models/nps-referral.model';
import { AuthorizationSchoolResponse } from '../../iep/iep-document/interfaces/authorization-school-response';
import { PlacementConsentModel } from '../models/placement-finder/save-genrate-parent-notification';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';

@Injectable({
  providedIn: 'root',
})
export class PlacementService<T extends ISedmsFormOutput> {
  config: IAppConfig;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  // TODO: The return type of this method should be `ApiResponse<Placement>` but currently getting a lot of errors. Will need to fix at a later point in time
  public submit(request: T): Observable<any> {
    return this.http
      .post<any>(`${this.config.apiUrl}/PlacementFinder/Add`, request)
      .pipe(catchError(handleError));
  }

  public search(request: T): Observable<SchoolDetails[]> {
    return this.http
      .post<SchoolDetails[]>(
        `${this.config.apiUrl}/SchoolAgePlacementFinder/Search`,
        request
      )
      .pipe(catchError(handleError));
  }

  // TODO: The return type of this method should be `Placement` but currently getting a lot of errors. Will need to fix at a later point in time
  public get(consentSk?: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/placementFinder/Get/${encodeURIComponent(consentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(
        map((res: any) => (!res ? {} : res)),
        catchError(handleError)
      );
  }
  public preschoolPFSearch(request: T): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/PlacementFinder/Search`, request)
      .pipe(catchError(handleError));
  }

  public getNpsSchools(
    locationName?: string,
    locationCode?: string
  ): Observable<any> {
    return this.http
      .get<any[]>(`${this.config.apiUrl}/NPSReferral/GetNPSSchools`, {
        params: {
          locationName: locationName || '',
          locationCode: locationCode || '',
        },
      })
      .pipe(
        map((res: any) => (!res ? [] : res)),
        catchError(handleError)
      );
  }

  public npsReferralAdd(request: NpsReferralRequest): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/NPSReferral/Add`, request)
      .pipe(catchError(handleError));
  }

  public respondToNps(request: NpsReferralRequest): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/NPSReferral/RespondToNPS`, request)
      .pipe(catchError(handleError));
  }

  public getNpsSchoolsInReview(caseActionSk: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/NPSReferral/Get/${encodeURIComponent(caseActionSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(
        map((res: any) => (!res ? [] : res)),
        catchError(handleError)
      );
  }

  public getNpsReferralPackages(studentSk: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/NPSReferral/GetReferralPackage/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(
        map((res: any) => (!res ? [] : res)),
        catchError(handleError)
      );
  }

  public getNpsCoverForReview(caseActionSk: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/NPSReferral/ViewNPSCover/${encodeURIComponent(caseActionSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any>(url)
      .pipe(
        map((res) => res || {}),
        catchError(handleError)
      );
  }

  public reviewCbstCoverLetter(request: NpsReferralRequest): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/NPSReferral/ReviewCBSTCoverLetter`, request)
      .pipe(catchError(handleError));
  }

  public getPlacementDetails(
    studentSk?: string,
    type?: string
  ): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/placementFinder/PlacementDetails/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(
        map((res: any) => (!res ? {} : res)),
        catchError(handleError)
      );
  }
  public getAuthorizationSchoolDetails(studentSk: string): Observable<AuthorizationSchoolResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/SchoolAgePlacementFinder/AuthorizationSchool/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AuthorizationSchoolResponse>(url)
      .pipe(catchError(handleError));
  }
  public getSchoolAgePlacementDetails(
    caseActionSk?: string,
    type?: string
  ): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/SchoolAgePlacementFinder/GET/${encodeURIComponent(caseActionSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(
        map((res: any) => (!res ? {} : res)),
        catchError(handleError)
      );
  }

  public getOfficialClassDetails(payload: {
    schoolDBN: string;
    borough: string;
    district: string;
    locationCode: string;
    buildingCode: string;
    isSummer: boolean;
    scscis: string[];
  }): Observable<any> {
    return this.http.post<any>(
      `${this.config.apiUrl}/SchoolAgePlacementFinder/OfficialClassDetails`,
      payload
    );
  }

  public submitVarianceRequest(request): Observable<any> {
    return this.http
      .post<any[]>(
        `${this.config.apiUrl}/PlacementFinder/VarianceRequestAdd`,
        request
      )
      .pipe(catchError(handleError));
  }

  public getVarianceList(varianceId: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PlacementFinder/VarianceRequest/${encodeURIComponent(varianceId)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(catchError(handleError));
  }

  public getSchoolProfile(
    locationCode: string,
    buildingCode: string
  ): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/PreschoolProfile/${encodeURIComponent(locationCode)}/${encodeURIComponent(buildingCode)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(catchError(handleError));
  }

  public getPreSchoolClasses(
    locationCode: string,
    buildingCode: string,
    schoolyear: string
  ): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/GetPreschoolClasses/${encodeURIComponent(locationCode)}/${encodeURIComponent(buildingCode)}/${encodeURIComponent(schoolyear)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(catchError(handleError));
  }
  public submitPreschoolPlacementFinder(request): Observable<any> {
    return this.http
      .post<any[]>(`${this.config.apiUrl}/PlacementFinder/Add`, request)
      .pipe(catchError(handleError));
  }
  public submitSchoolAgePlacement(request: T): Observable<any> {
    return this.http
      .post(`${this.config.apiUrl}/SchoolAgePlacementFinder/Create`, request)
      .pipe(catchError(handleError));
  }
  public getVarianceLists(classSk: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/PreschoolProfile/GetVarianceList/${encodeURIComponent(classSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<any[]>(url)
      .pipe(catchError(handleError));
  }
  public submitRequestVariance(request): Observable<any> {
    return this.http
      .post<any[]>(
        `${this.config.apiUrl}/PlacementFinder/VarianceRequestAdd`,
        request
      )
      .pipe(catchError(handleError));
  }
  public getParentNotification(id: string): Observable<PlacementConsentModel> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/SchoolAgePlacementFinder/ViewCollegeAccommodationLetter/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<PlacementConsentModel>(url)
      .pipe(catchError(handleError));
  }
  public submitParentNotification(request: PlacementConsentModel): Observable< ApiResponse<PlacementConsentModel>> {
    return this.http
      .post<ApiResponse<PlacementConsentModel>>(
        `${this.config.apiUrl}/SchoolAgePlacementFinder/CreateCollegeAccommodationLetter`,
        request
      )
      .pipe(catchError(handleError));
  }
}
